import { Modal, Button, addToast } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Container, Col, Row } from 'reactstrap';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { TranslationsKeys } from '../../../locales/translations';
import {
  Announcement,
  AnnouncementFormFields,
  AnnouncementFormRequest,
} from '../types';
import AnnouncementEditForm from './AnnouncementEditForm';
import { convertToFormFile, convertToSelectOption } from '../utils';
import AnnouncementModalLoader from './Loaders/AnnouncementModalLoader';
import { create, update } from '../api';

export type AnnouncementEditModalMethods = {
  open: (announcement?: Announcement) => void;
  close: () => void;
};

type AnnouncementEditModalProps = {
  onSave?: () => void;
};

const DEFAULT_VALUES: AnnouncementFormFields = {
  title: '',
  description: '',
  publishAt: '',
  validUntilAt: '',
  academicLevel: null,
  studyPlan: null,
  school: null,
  targets: [],
  supplementaryFiles: [],
};

const AnnouncementEditModal = (
  { onSave }: AnnouncementEditModalProps,
  ref: Ref<AnnouncementEditModalMethods>,
) => {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_ANNOUNCEMENTS_MODAL,
  );

  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [announcement, setAnnouncement] = useState<Announcement>();

  const form = useForm<AnnouncementFormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const open = useCallback(
    (row?: Announcement) => {
      setAnnouncement(row);
      form.reset({
        title: row?.title?.trim() || '',
        description: row?.description?.trim() || '',
        publishAt: row?.publishAt?.trim() || '',
        validUntilAt: row?.validUntilAt?.trim() || '',
        academicLevel: convertToSelectOption(row?.academicLevel),
        school: convertToSelectOption(row?.school),
        studyPlan: convertToSelectOption(row?.studyPlan),
        targets: row?.targets || [],
        supplementaryFiles:
          row?.supplementaryFiles?.map(convertToFormFile) || [],
      });
      setVisible(true);
    },
    [form],
  );

  const close = useCallback(() => {
    setVisible(false);
    setAnnouncement(undefined);
    form.reset(DEFAULT_VALUES);
  }, [form]);

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }
    close();
  }, [close, saving]);

  const handleUpdate = useCallback(
    async (payload: AnnouncementFormRequest) => {
      if (saving || !announcement?.id) {
        return;
      }
      setSaving(true);
      const { error } = await update(announcement?.id, payload);
      setSaving(false);
      if (error) {
        return addToast({
          icon: 'error',
          color: 'danger',
          text: t('update.failed'),
        });
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`update.success`),
      });

      close();
      !!onSave && onSave();
    },
    [saving, announcement?.id, close, onSave, t],
  );
  const handleCreate = useCallback(
    async (payload: AnnouncementFormRequest) => {
      if (saving) {
        return;
      }
      setSaving(true);
      const { error } = await create(payload);
      setSaving(false);
      if (error) {
        return addToast({
          icon: 'error',
          color: 'danger',
          text: t('create.failed'),
        });
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`create.success`),
      });

      close();
      !!onSave && onSave();
    },
    [saving, close, onSave, t],
  );

  const handleSubmit = useCallback(
    async (values: AnnouncementFormFields) => {
      const payload: AnnouncementFormRequest = {
        ...values,
        schoolId: values?.school?.value ? +values?.school?.value : null,
        studyPlanId: values?.studyPlan?.value
          ? +values?.studyPlan?.value
          : null,
        academicLevelId: values?.academicLevel?.value
          ? +values?.academicLevel?.value
          : null,
        supplementaryFiles: values?.supplementaryFiles?.map((e) => e.id) || [],
      };
      if (announcement?.id) {
        await handleUpdate(payload);
      } else {
        await handleCreate(payload);
      }
    },
    [announcement?.id, handleUpdate, handleCreate],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Modal isOpen={visible} size="lg" toggle={handleClose}>
      <FormProvider {...form}>
        <Container>
          <Row>
            <Col xs={12}>
              <h1 className="text-dark text-center fw-600 fs-20">
                {announcement?.id ? t(`update.title`) : t(`create.title`)}
              </h1>
              <p className=" text-center fs-16 mb-4">
                {announcement?.id
                  ? t(`update.description`)
                  : t(`create.description`)}
              </p>
            </Col>
          </Row>
        </Container>
        <AnnouncementModalLoader>
          <AnnouncementEditForm />
        </AnnouncementModalLoader>
        <Container>
          <Row className="pt-5">
            <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
              <Button
                type="button"
                outlined
                disabled={saving}
                onClick={handleClose}
                text={t(`common:actions.close`)}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
              <Button
                type="button"
                loading={saving}
                text={t(`common:actions.save`)}
                onClick={form.handleSubmit(handleSubmit)}
                fullwidth
              />
            </Col>
          </Row>
        </Container>
      </FormProvider>
    </Modal>
  );
};

export default forwardRef(AnnouncementEditModal);
